import { createElement, useEffect, type ComponentProps, type MouseEvent } from "react";
import { Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";

/** React Router records its own position in `history.state.idx`; 0 is the first entry this app pushed. */
const historyIdx = () => (window.history.state as { idx?: number } | null)?.idx ?? 0;

/** The pathname of each history entry this tab has shown, by `idx` — lost on a reload, which only
 * means `BackLink` falls back to its plain link. */
const visited: string[] = [];

/** Mounted once, in the route tree's root layout, so every entry is recorded. */
export function useRecordVisits(): void {
  const { key, pathname } = useLocation();
  useEffect(() => {
    visited[historyIdx()] = pathname;
  }, [key, pathname]);
}

/**
 * Where a form's Save and Cancel both go: back to the screen the user came from (a filtered list
 * on page 3, a product page, the dashboard) — a history step, so its filters, paging and search come
 * back exactly as they were and its loader re-reads the saved record — or to `fallback` when there
 * is no in-app screen to go back to: the form was opened from a bookmark, a new tab or a
 * notification link. A create that goes on to the new record's own page instead navigates there
 * with `replace: true`, so that page's Save/Cancel return to the list rather than the empty form.
 */
export function useGoBack(fallback: string): () => void {
  const navigate = useNavigate();
  return () => {
    if (historyIdx() > 0) navigate(-1);
    else navigate(fallback);
  };
}

/**
 * A "Back to …" link that names its destination. When the previous entry is that same screen it
 * steps back instead, so the list returns with its filters and page; otherwise it is a plain link,
 * so it never lands anywhere but where its label says.
 */
export function BackLink({ to, onClick, ...props }: ComponentProps<typeof Link> & { to: string }) {
  const navigate = useNavigate();
  return createElement(Link, {
    ...props,
    to,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      const idx = historyIdx();
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (idx > 0 && visited[idx - 1] === new URL(to, window.location.origin).pathname) {
        event.preventDefault();
        navigate(-1);
      }
    },
  });
}

/** Each list's last query (filters, page, page size), by pathname — written by the list's own table. */
const listSearch = new Map<string, string>();

export function rememberListSearch(pathname: string, search: string): void {
  listSearch.set(pathname, search);
}

/** Whether `pathname` is one of `list`'s own screens — a record, a form or a sub-screen under it. */
const isUnder = (pathname: string | undefined, list: string) => Boolean(pathname?.startsWith(`${list}/`));

/**
 * Where a navigation to a bare list URL lands instead: that list as it was left, when the screen
 * being left is one of its own (a record's breadcrumb, its Back, Save, the sidebar entry). A link
 * that carries its own query, Reset, or the list reached from anywhere else goes where it says.
 * Read by the router's `dataStrategy` before any loader runs, while the history entry is still the
 * screen being left.
 */
export function listReturn(to: URL): string | undefined {
  const search = listSearch.get(to.pathname);
  if (to.search || !search || !isUnder(visited[historyIdx()], to.pathname)) return undefined;
  return to.pathname + search;
}

/** Whether this screen was returned to — Back/Forward, or a link from one of its own screens. */
export function useReturned(): boolean {
  const { pathname } = useLocation();
  return useNavigationType() === "POP" || isUnder(visited[historyIdx() - 1], pathname);
}
